import { DeeplinkActionHandler } from "@leagueplatform/masonry";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { MASONRY } from "common/constants";
import { awaitMasonryNodePrefetch } from "utils/masonry-node-prefetch";

/**
 * Quest override for the Masonry `deeplink` action.
 *
 * The SDK's default `DeeplinkActionHandler` short-circuits for app-picker
 * URI schemes (`tel:` / `mailto:` / `sms:`) by *not* calling
 * `event.preventDefault()` — it expects the browser to honor the native
 * default behavior of an underlying `<a href="tel:…">` element. That
 * works when the click originates from an anchor (mobile-web `<a>` tap),
 * but on cards/buttons whose `onClick` is wired through a JS handler
 * (e.g. the BE `callServiceCenterCard` `extraSmallCard` node), there's no
 * default behavior to honor and the action silently no-ops.
 *
 * This handler explicitly drives the navigation via
 * `window.location.href` for those schemes so the OS dialer / mail
 * client / SMS composer opens regardless of how the click was wired.
 * Everything else (in-app deep links starting with `/`, external
 * `http(s)://` URLs) is delegated to the SDK default.
 *
 * Surfaced by: `customerSupport$root` (QUEST-1862). Applies globally so
 * any future `tel:` / `mailto:` / `sms:` actions in Quest masonry configs
 * just work.
 */
const APP_PICKER_SCHEME = /^(tel:|mailto:|sms:)/i;

/**
 * The invoking CTA stays in its loading state until the whole action chain
 * resolves, so awaiting a prefetch here keeps the spinner on the button instead
 * of mounting the destination empty.
 */
const PREFETCHED_ROUTE_NODE_IDS: Record<string, string> = {
  [APP_MANAGED_ROUTES.SELF_COLLECTION_ORDER_PLACED]:
    MASONRY.ROOT_NODE_ID_SELF_COLLECTION_ORDER_PLACED,
  [APP_MANAGED_ROUTES.SELF_COLLECTION_ORDER_CANCELLED]:
    MASONRY.ROOT_NODE_ID_SELF_COLLECTION_ORDER_CANCELLED,
};

export const QuestDeeplinkActionHandler: typeof DeeplinkActionHandler = async (
  action,
  resources
) => {
  const url = action.payload?.url;

  if (typeof url === "string" && APP_PICKER_SCHEME.test(url)) {
    window.location.href = url;
    return undefined;
  }

  const prefetchedNodeId =
    typeof url === "string" ? PREFETCHED_ROUTE_NODE_IDS[url] : undefined;

  if (prefetchedNodeId) {
    await awaitMasonryNodePrefetch(prefetchedNodeId);
  }

  return DeeplinkActionHandler(action, resources);
};
